/**
 * @module lib/house/elements/window/windowTypecheck
 * @note I would not mind if all of these became static methods on a Window class, which would assist in lazy evaluation
 */
import {
  isValidOpening,
  wallOpeningOpening2,
  wallOpeningOpening3,
  wallOpeningPlane,
  wallOpeningRect2,
} from "building-common/openings/openingQuery";
import { profile2OrientCopy } from "building-common/profile2Utils";
import { StreetOrientationMode } from "building-types/building/streetOrientationMode";
import { FinishClipping } from "building-types/casing/casingTypes";
import type { OpeningTrim } from "building-types/casing/openingTrimTypes";
import type { FloorAssembly } from "building-types/floors/floorAssemblyTypes";
import { OpeningWrappingModeEnum } from "building-types/openings/openingWrappingModes";
import type { ExteriorWindowCasingSettings } from "building-types/settings/exteriorWindowCasingSettings";
import type { InteriorWindowCasingSettings } from "building-types/settings/interiorWindowCasingSettings";
import {
  ApronCasingType,
  HeadCasingType,
  SideCasingType,
  StoolCasingType,
} from "building-types/settings/settingsTypesEnum";
import { WindowPanelType, WindowShapeTypes } from "building-types/settings/windowTypesEnum";
import type { GeneratedWindowPlacement, WindowDescriptor } from "building-types/windows/windowTypes";
import type { Table } from "common/data/table";
import type { LevelInches } from "common/types/nominalNumbersTypes";
import { ensureDefined } from "common/utils/assertOfType";
import { floorAssemblyBaseOffset } from "doc/elements/floor/floorAssembly";
import { isExteriorWindowCasingSettings, isInteriorWindowCasingSettings } from "doc/settingsTools";
import { DIST_EPSILON } from "geom/core";
import { polyLine2SetCCW } from "geom/polyLine2";
import { checkOrientationWithMode } from "../streetOrientation";
import { getWindowHeight, windowHeadHeight } from "./windowHeightTools";

function isValidDescriptor(descriptor: GeneratedWindowPlacement["descriptor"]): descriptor is WindowDescriptor {
  // NOTE: add more checks as needed to validate a descriptor
  if (!descriptor) {
    return false;
  }
  return true;
}

export function windowIsRectangle(window: Pick<GeneratedWindowPlacement, "descriptor">) {
  if (!isValidDescriptor(window.descriptor)) {
    return false;
  }
  return (
    window.descriptor.shape === WindowShapeTypes.RECTANGLE ||
    window.descriptor.shape === WindowShapeTypes.RECTANGLE_ARCHED_TOP
  );
}

export function windowIsIrregular(window: Pick<GeneratedWindowPlacement, "descriptor">) {
  if (!isValidDescriptor(window.descriptor)) {
    return false;
  }
  return (
    window.descriptor.shape === WindowShapeTypes.CIRCLE ||
    window.descriptor.shape === WindowShapeTypes.HALF_ROUND ||
    window.descriptor.shape === WindowShapeTypes.ELLIPSE ||
    window.descriptor.shape === WindowShapeTypes.POLYGONAL
  );
}

export function windowHasCasing(
  window: Pick<GeneratedWindowPlacement, "descriptor" | "streetOrientation">,
  casingSettings: ExteriorWindowCasingSettings | InteriorWindowCasingSettings | null | undefined,
): boolean {
  if (!isValidDescriptor(window.descriptor)) {
    return false;
  }
  if (!casingSettings) {
    return false;
  }
  if (isInteriorWindowCasingSettings(casingSettings)) {
    return !!casingSettings.includeCasing;
  }
  if (
    casingSettings?.casingOrientation &&
    !checkOrientationWithMode(window.streetOrientation, casingSettings.casingOrientation)
  ) {
    return false;
  }
  return (
    windowHasApronCasing(window, casingSettings.apronCasingType) ||
    windowHasHeadCasing(window, casingSettings.headCasingType) ||
    windowHasStoolCasing(window, casingSettings.stoolCasingType) ||
    windowHasSideCasing(casingSettings)
  );
}

export function windowCasingClipsFinish(
  window: Pick<GeneratedWindowPlacement, "descriptor" | "exteriorCasing" | "streetOrientation">,
  exteriorCasing: ExteriorWindowCasingSettings | null,
): boolean {
  if (!windowHasCasing(window, exteriorCasing)) {
    return false;
  }
  return (
    windowApronCasingClipsFinish(window, exteriorCasing) ||
    windowSideCasingClipsFinish(window, exteriorCasing) ||
    windowHeadCasingClipsFinish(window, exteriorCasing) ||
    windowStoolCasingClipsFinish(window, exteriorCasing)
  );
}

export function windowHeadCasingClipsFinish(
  window: Pick<GeneratedWindowPlacement, "descriptor" | "exteriorCasing" | "streetOrientation">,
  exteriorCasing: ExteriorWindowCasingSettings | null,
): boolean {
  if (!isValidDescriptor(window.descriptor)) {
    return false;
  }

  return (
    checkOrientationWithMode(window.streetOrientation, casingOrientationMode(exteriorCasing)) &&
    windowHasHeadCasing(window, exteriorCasing?.headCasingType) &&
    exteriorCasing?.headCasingFinishClipping === FinishClipping.CLIP_FINISH
  );
}

export function windowSideCasingClipsFinish(
  window: Pick<GeneratedWindowPlacement, "streetOrientation">,
  exteriorCasing: ExteriorWindowCasingSettings | null,
): boolean {
  return (
    checkOrientationWithMode(window.streetOrientation, casingOrientationMode(exteriorCasing)) &&
    windowHasSideCasing(exteriorCasing) &&
    exteriorCasing?.sideCasingFinishClipping === FinishClipping.CLIP_FINISH
  );
}

export function windowApronCasingClipsFinish(
  window: Pick<GeneratedWindowPlacement, "descriptor" | "exteriorCasing" | "streetOrientation">,
  exteriorCasing: ExteriorWindowCasingSettings | null,
): boolean {
  if (!isValidDescriptor(window.descriptor)) {
    return false;
  }

  return (
    checkOrientationWithMode(window.streetOrientation, casingOrientationMode(exteriorCasing)) &&
    windowHasApronCasing(window, exteriorCasing?.apronCasingType) &&
    exteriorCasing?.apronCasingFinishClipping === FinishClipping.CLIP_FINISH
  );
}

export function windowStoolCasingClipsFinish(
  window: Pick<GeneratedWindowPlacement, "descriptor" | "exteriorCasing" | "streetOrientation">,
  exteriorCasing: ExteriorWindowCasingSettings | null,
): boolean {
  if (!isValidDescriptor(window.descriptor)) {
    return false;
  }

  return (
    checkOrientationWithMode(window.streetOrientation, casingOrientationMode(exteriorCasing)) &&
    windowHasStoolCasing(window, exteriorCasing?.stoolCasingType) &&
    exteriorCasing?.stoolCasingFinishClipping === FinishClipping.CLIP_FINISH
  );
}

export function windowTrimClipsExteriorFinish(window: GeneratedWindowPlacement, trim: OpeningTrim): boolean {
  const exteriorCasing = window.exteriorCasing ? (window.exteriorCasing as ExteriorWindowCasingSettings) : null;

  switch (trim.type) {
    case "HEAD_CASING":
    case "KEYSTONE":
      return windowHeadCasingClipsFinish(window, exteriorCasing);
    case "SIDE_CASING":
      return windowSideCasingClipsFinish(window, exteriorCasing);
    case "APRON":
      return windowApronCasingClipsFinish(window, exteriorCasing);
    case "STOOL":
      return windowStoolCasingClipsFinish(window, exteriorCasing);
  }
}

/**
 * if exterior casing is not defined, default to FRONT_AND_SIDE_FACES
 */
function casingOrientationMode(exteriorCasing: ExteriorWindowCasingSettings | null): StreetOrientationMode {
  if (exteriorCasing && exteriorCasing?.casingOrientation) {
    return exteriorCasing.casingOrientation;
  }
  return StreetOrientationMode.FRONT_AND_SIDE_FACES;
}

export function windowHasApronCasing(
  window: Pick<GeneratedWindowPlacement, "descriptor" | "exteriorCasing">,
  apronCasingType: ApronCasingType | undefined,
): boolean {
  if (!isValidDescriptor(window.descriptor)) {
    return false;
  }
  return apronCasingType === ApronCasingType.TRIM && windowHasSill(window.descriptor);
}

export function windowHasSideCasing(
  casingSettings: ExteriorWindowCasingSettings | InteriorWindowCasingSettings | null,
): boolean {
  if (!casingSettings) {
    return false;
  }
  if (isExteriorWindowCasingSettings(casingSettings)) {
    return casingSettings.sideCasingType === SideCasingType.TRIM;
  }
  return !!casingSettings.includeCasing;
}

export function windowHasHeadCasing(
  window: Pick<GeneratedWindowPlacement, "descriptor">,
  headCasingType: HeadCasingType | undefined,
): boolean {
  if (!isValidDescriptor(window.descriptor)) {
    return false;
  }
  return (
    headCasingType !== HeadCasingType.NONE &&
    window.descriptor.shape !== WindowShapeTypes.CIRCLE &&
    window.descriptor.shape !== WindowShapeTypes.HALF_ROUND &&
    window.descriptor.shape !== WindowShapeTypes.ELLIPSE
  );
}

export function windowHasStoolCasing(
  window: Pick<GeneratedWindowPlacement, "descriptor">,
  stoolCasingType: StoolCasingType | undefined,
): boolean {
  if (!isValidDescriptor(window.descriptor)) {
    return false;
  }
  return stoolCasingType === StoolCasingType.TRIM && windowHasSill(window.descriptor);
}

export function windowHasUnsupportedHeadCasing(
  window: Pick<GeneratedWindowPlacement, "descriptor">,
  exteriorCasing: ExteriorWindowCasingSettings | null,
): boolean {
  if (!isValidDescriptor(window.descriptor)) {
    return false;
  }
  const { shape: windowShape } = window.descriptor;
  if (!exteriorCasing) {
    return false;
  }
  const { headCasingType } = exteriorCasing;
  return (
    ((windowShape === WindowShapeTypes.RECTANGLE && window.descriptor.arched) ||
      windowShape === WindowShapeTypes.RECTANGLE_ARCHED_TOP) &&
    (headCasingType === HeadCasingType.SLICES ||
      headCasingType === HeadCasingType.STONE ||
      headCasingType === HeadCasingType.JACK_ARCH ||
      headCasingType === HeadCasingType.ARCHED_SLICES ||
      headCasingType === HeadCasingType.EYEBROW_TRIM)
  );
}

export function windowHasSill(descriptor: WindowDescriptor): boolean {
  switch (descriptor.shape) {
    case WindowShapeTypes.CIRCLE:
    case WindowShapeTypes.ELLIPSE:
      return false;
    default:
      return true;
  }
}

export function windowDescriptorShouldCutWall(window: GeneratedWindowPlacement) {
  return window.materials?.panelType !== WindowPanelType.FAUX_SHUTTER;
}

export function windowHasJamb(window: Pick<GeneratedWindowPlacement, "descriptor">) {
  return !window?.descriptor?.open;
}

export function windowHasWrapping(window: Pick<GeneratedWindowPlacement, "descriptor" | "wrapping">) {
  if (!!window.wrapping) {
    return window.wrapping?.wrappingMode !== OpeningWrappingModeEnum.NONE;
  }
  if (!window.descriptor.wrapping) {
    return false;
  }
  return window.descriptor.wrapping.wrappingMode !== OpeningWrappingModeEnum.NONE;
}

type OpeningQueryWindow = Pick<GeneratedWindowPlacement, "opening" | "hasExteriorCasing">;

export function windowBackOpeningRect2(window: OpeningQueryWindow) {
  if (!isValidOpening(window.opening)) {
    return undefined;
  }
  return profile2OrientCopy(polyLine2SetCCW(wallOpeningRect2(ensureDefined(window.opening), "BACK")));
}

export function windowFrontOpeningRect2(window: OpeningQueryWindow) {
  if (!isValidOpening(window.opening)) {
    return undefined;
  }
  return wallOpeningRect2(ensureDefined(window.opening), "FRONT");
}

export function windowBackPlane(window: OpeningQueryWindow) {
  if (!isValidOpening(window.opening)) {
    return undefined;
  }
  return wallOpeningPlane(ensureDefined(window.opening), "BACK");
}

export function windowBackOpening2(window: OpeningQueryWindow, includeCasing: boolean = true) {
  if (!isValidOpening(window.opening)) {
    return undefined;
  }
  return wallOpeningOpening2(ensureDefined(window.opening), "BACK", !!window.hasExteriorCasing && includeCasing);
}

export function windowFrontOpening2(window: OpeningQueryWindow, includeCasing: boolean = true) {
  if (!isValidOpening(window.opening)) {
    return undefined;
  }
  return wallOpeningOpening2(ensureDefined(window.opening), "FRONT", !!window.hasExteriorCasing && includeCasing);
}

export function windowFrontPlane(window: OpeningQueryWindow) {
  if (!isValidOpening(window.opening)) {
    return undefined;
  }
  return wallOpeningPlane(ensureDefined(window.opening), "FRONT");
}

export function windowBackOpening3(window: OpeningQueryWindow, includeCasing: boolean = true) {
  if (!isValidOpening(window.opening)) {
    return undefined;
  }
  return wallOpeningOpening3(ensureDefined(window.opening), "BACK", !!window.hasExteriorCasing && includeCasing);
}

export function windowFrontOpening3(window: OpeningQueryWindow, includeCasing: boolean = true) {
  if (!isValidOpening(window.opening)) {
    return undefined;
  }
  return wallOpeningOpening3(ensureDefined(window.opening), "FRONT", !!window.hasExteriorCasing && includeCasing);
}

export function windowIntersectsCutPlane(
  windowPlacement: GeneratedWindowPlacement,
  cutPlaneHeight: LevelInches | undefined,
  roomFloorAssemblies?: Table<FloorAssembly>,
): boolean {
  if (!windowDescriptorShouldCutWall(windowPlacement)) {
    return false;
  }

  if (!windowPlacement.opening) {
    return false;
  }
  if (cutPlaneHeight === undefined) {
    return true;
  }

  let offset = 0;
  if (roomFloorAssemblies && windowPlacement.frontRoom && roomFloorAssemblies[windowPlacement.frontRoom.id]) {
    offset = floorAssemblyBaseOffset(roomFloorAssemblies[windowPlacement.frontRoom.id]);
  }
  const top = windowHeadHeight(windowPlacement) + offset;
  const bottom = top - getWindowHeight(windowPlacement.descriptor);

  return bottom - DIST_EPSILON < cutPlaneHeight && top + DIST_EPSILON > cutPlaneHeight;
}
